import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, RefreshCw, Trash2, UserCircle2 } from "lucide-react";
import { controlApi } from "./api";
import type { NexusUser } from "./types";
import { FiltersBar } from "../applications/shared/FiltersBar";
import {
  ApplicationsTable,
  type Column,
} from "../applications/shared/ApplicationsTable";
import { formatDateTime } from "../applications/shared/format";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { useDebounce } from "@/lib/utils/useDebounce";

export function UsersScreen() {
  const [search, setSearch] = useState("");
  const [roleSlug, setRoleSlug] = useState("");
  const [grantOpen, setGrantOpen] = useState(false);

  const debouncedSearch = useDebounce(search, 300);
  const qc = useQueryClient();
  const { push } = useToast();

  const usersQuery = useQuery({
    queryKey: ["nexus-control", "users", { q: debouncedSearch, roleSlug }],
    queryFn: () =>
      controlApi.listUsers({
        q: debouncedSearch || undefined,
        roleSlug: roleSlug || undefined,
      }),
    placeholderData: (prev) => prev,
  });

  const rolesQuery = useQuery({
    queryKey: ["nexus-control", "roles"],
    queryFn: () => controlApi.listRoles(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["nexus-control", "users"] });

  const grant = useMutation({
    mutationFn: ({ email, roleSlug }: { email: string; roleSlug: string }) =>
      controlApi.grantRole(email, roleSlug),
    onSuccess: () => {
      push({ kind: "success", message: "Rol asignado." });
      invalidate();
      setGrantOpen(false);
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "Error al asignar rol." }),
  });

  const revoke = useMutation({
    mutationFn: ({ userId, roleSlug }: { userId: number; roleSlug: string }) =>
      controlApi.revokeRole(userId, roleSlug),
    onSuccess: () => {
      push({ kind: "success", message: "Rol revocado." });
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "Error al revocar." }),
  });

  const roleOptions = useMemo(() => {
    return (rolesQuery.data?.roles ?? []).map((r) => ({
      value: r.slug,
      label: r.name,
    }));
  }, [rolesQuery.data]);

  const handleRevoke = async (userId: number, slug: string, roleName: string) => {
    const ok = await confirmAction({
      title: `Revocar rol "${roleName}"`,
      message: "El usuario pierde los permisos asociados. Si era su único rol, pierde acceso a Nexus.",
      confirmLabel: "Revocar",
      tone: "danger",
    });
    if (ok === true) revoke.mutate({ userId, roleSlug: slug });
  };

  const columns: Column<NexusUser>[] = [
    {
      key: "user",
      header: "Usuario",
      cell: (u) => (
        <div className="flex items-center gap-2.5 min-w-0">
          {u.avatarUrl ? (
            <img
              src={u.avatarUrl}
              alt=""
              className="w-8 h-8 rounded-full object-cover border border-border shrink-0"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-bg-subtle border border-border shrink-0 flex items-center justify-center">
              <UserCircle2 size={16} className="text-fg-subtle" />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-medium text-fg truncate">{u.name}</div>
            <div className="text-2xs text-fg-subtle break-all">{u.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: "roles",
      header: "Roles",
      cell: (u) => (
        <div className="flex flex-wrap gap-1">
          {u.roles.map((r) => (
            <span
              key={r.id}
              className="inline-flex items-center gap-1 nx-badge-info group"
              title={`Asignado ${formatDateTime(r.grantedAt)}`}
            >
              {r.name}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRevoke(u.id, r.slug, r.name);
                }}
                disabled={revoke.isPending}
                className="opacity-50 hover:opacity-100 transition-opacity"
                aria-label={`Revocar rol ${r.name}`}
              >
                <Trash2 size={9} />
              </button>
            </span>
          ))}
        </div>
      ),
    },
    {
      key: "lastSeen",
      header: "Última actividad",
      cell: (u) => (
        <span className="text-fg-subtle text-xs">
          {u.lastSeenAt ? formatDateTime(u.lastSeenAt) : "—"}
        </span>
      ),
      width: "min",
      align: "right",
    },
  ];

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Nexus Control · Usuarios</p>
          <h1 className="nx-h1">Usuarios con acceso a Nexus</h1>
          <p className="text-sm text-fg-muted mt-1">
            Asigná o revocá roles. Un usuario debe haberse logueado al menos
            una vez en mixcore.club o nexus.mixcore.club (Google Sign-In)
            antes de recibir un rol.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setGrantOpen(true)} className="nx-btn-primary">
            <Plus size={14} /> Asignar rol
          </button>
          <button
            onClick={() => usersQuery.refetch()}
            disabled={usersQuery.isFetching}
            className="nx-btn-secondary"
          >
            <RefreshCw size={14} className={usersQuery.isFetching ? "animate-spin" : ""} />
            Actualizar
          </button>
        </div>
      </header>

      <FiltersBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por nombre o email…"
        status={roleSlug}
        onStatusChange={setRoleSlug}
        statusOptions={roleOptions}
      />

      {usersQuery.isLoading ? (
        <LoadingBlock label="Cargando usuarios…" />
      ) : usersQuery.isError ? (
        <ErrorBlock onRetry={() => usersQuery.refetch()} />
      ) : (
        <ApplicationsTable<NexusUser>
          rows={usersQuery.data?.users ?? []}
          columns={columns}
          page={1}
          pageSize={(usersQuery.data?.users ?? []).length || 1}
          total={(usersQuery.data?.users ?? []).length}
          onPageChange={() => {}}
          emptyLabel="No hay usuarios con rol Nexus asignado."
        />
      )}

      {grantOpen && (
        <GrantModal
          onClose={() => setGrantOpen(false)}
          onSubmit={(email, roleSlug) => grant.mutate({ email, roleSlug })}
          submitting={grant.isPending}
          roles={rolesQuery.data?.roles ?? []}
        />
      )}
    </div>
  );
}

function GrantModal({
  onClose,
  onSubmit,
  submitting,
  roles,
}: {
  onClose: () => void;
  onSubmit: (email: string, roleSlug: string) => void;
  submitting: boolean;
  roles: { slug: string; name: string; description: string }[];
}) {
  const [email, setEmail] = useState("");
  const [roleSlug, setRoleSlug] = useState(roles[0]?.slug ?? "");

  const selectedRole = roles.find((r) => r.slug === roleSlug);

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[90] animate-fade-in" onClick={onClose} aria-hidden />
      <div className="fixed inset-0 z-[91] flex items-center justify-center p-4 pointer-events-none">
        <div className="nx-card pointer-events-auto w-full max-w-md p-5 animate-fade-in">
          <h3 className="nx-h2 mb-2">Asignar rol Nexus</h3>
          <p className="text-2xs text-fg-muted mb-4">
            El email debe corresponder a un usuario que ya se haya logueado al menos una vez.
          </p>

          <div className="flex flex-col gap-3">
            <div>
              <label className="nx-eyebrow block mb-1">Email del usuario</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ej. nombre@mixcore.club"
                autoFocus
                className="nx-input"
              />
            </div>
            <div>
              <label className="nx-eyebrow block mb-1">Rol</label>
              <select
                value={roleSlug}
                onChange={(e) => setRoleSlug(e.target.value)}
                className="nx-input"
              >
                {roles.map((r) => (
                  <option key={r.slug} value={r.slug}>
                    {r.name}
                  </option>
                ))}
              </select>
              {selectedRole?.description && (
                <p className="text-2xs text-fg-subtle mt-1">{selectedRole.description}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button onClick={onClose} className="nx-btn-secondary" disabled={submitting}>
              Cancelar
            </button>
            <button
              onClick={() => {
                const e = email.trim();
                if (!e || !roleSlug) return;
                onSubmit(e, roleSlug);
              }}
              disabled={submitting || !email.trim() || !roleSlug}
              className="nx-btn-primary"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Asignar
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
